import dayjs from 'dayjs'
import React from 'react'

import { Translate } from '@hozana/intl/Translate'
import { useDispatch } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { QA } from '@hozana/tests/constants'

import type { TButtonProps } from 'elements/button/Button'
import { RoundButton } from 'elements/button/RoundButton'
import { Cell } from 'elements/layout/Cell'
import { Div } from 'elements/layout/Div'
import { Row } from 'elements/layout/Row'
import { Icon } from 'elements/media/Icon'
import { EllipsisMultiLines } from 'elements/text/EllipsisMultiLines'
import { Text } from 'elements/text/Text'

import { ICON } from 'config/icons'
import { deletePublication } from 'config/routines'

import type { TPublication } from 'modules/publication/types'
import { UserProfileLink } from 'modules/user/components/UserProfileLink'

export type TSpecialPublicationHeaderProps = {
  publication: TPublication
  onClickEdit: TButtonProps['onClick']
}

export const SpecialPublicationHeader: TFC<TSpecialPublicationHeaderProps> = ({ publication, onClickEdit }) => {
  const router = useRouter()
  const dispatch = useDispatch()

  const isDraft = publication.isDraft
  const date = isDraft ? publication.contentUpdatedAt || publication.updatedAt : publication.publishedDate

  return (
    <Div bg={isDraft ? 'black10' : 'yellow'}>
      <Row p="0 40px 0 10px" gutter="10px" noMargin>
        <Cell align="middleCenter" p={{ xs: '5px', sm: '10px' }}>
          <Icon name={ICON.CALENDAR} size="1em" color={isDraft ? 'black10' : 'white'} />
        </Cell>
        <Cell flex="1" align="middleLeft">
          <EllipsisMultiLines
            lines="5"
            color={isDraft ? 'black50' : 'white'}
            bold
            fontSize={{ xs: '0.9em', sm: '1em' }}
          >
            {isDraft ? (
              <>
                <Translate values={{ updatedAt: dayjs(date).format('LL') }}>
                  {publication.contentUpdatedBy?.name
                    ? 'trans:publication:publication.drafted-at-and-by'
                    : 'trans:publication:publication.drafted-at'}
                </Translate>
                {publication.contentUpdatedBy?.id && (
                  <UserProfileLink userId={publication.contentUpdatedBy.id}>
                    <Text bold vAlign="bottom" inline>
                      {publication.contentUpdatedBy.name}
                    </Text>
                  </UserProfileLink>
                )}
              </>
            ) : (
              <>
                <Translate>trans:publication:publication.scheduled-for</Translate>
                {dayjs(date).format('LL')}
                &nbsp;
                <Translate>trans:publication:word.at</Translate>
                &nbsp;
                {dayjs(date).format('LT')}
              </>
            )}
          </EllipsisMultiLines>
        </Cell>
        <Cell align="top">
          {isDraft && (
            <RoundButton icon={ICON.TRASH} onClick={() => dispatch(deletePublication({ publication, router }))}>
              trans:publication:word.remove
            </RoundButton>
          )}
          <RoundButton
            icon={ICON.PENCIL}
            color={isDraft ? 'black50' : 'white'}
            hoverColor={isDraft ? 'black50' : 'white'}
            data-testid={QA.COMMUNITY_CREATE_PUBLICATION.EDIT_BUTTON}
            onClick={onClickEdit}
          />
        </Cell>
      </Row>
    </Div>
  )
}
